// src/util/progress.ts
import { useCallback, useEffect, useRef, useState } from "react";

interface ProgressValues {
  x: number;
  y: number;
}

interface UseProgressOptions {
  x?: number;
  y?: number;
  /** Whether to track vertical (y) axis. Default: false */
  trackY?: boolean;
  /** Called on every value change */
  onChange?: (values: ProgressValues) => void;
}

type UseProgressReturn = [
  ref: React.RefObject<HTMLDivElement>,
  percent: ProgressValues,
];

const clamp = (value: number, min = 0, max = 100): number =>
  Math.min(Math.max(value, min), max);

const useProgress = (options: UseProgressOptions = {}): UseProgressReturn => {
  const {
    x: initialX = 0,
    y: initialY = 0,
    trackY = false,
    onChange,
  } = options;

  const ref: any = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);

  const [percent, setPercent] = useState<ProgressValues>({
    x: clamp(initialX),
    y: clamp(initialY),
  });

  const calculate = useCallback(
    (clientX: number, clientY: number) => {
      const el = ref.current;
      if (!el) return;

      const rect = el.getBoundingClientRect();

      const newX = clamp(((clientX - rect.left) / rect.width) * 100);
      const newY = trackY
        ? clamp(((clientY - rect.top) / rect.height) * 100)
        : percent.y;

      const next: ProgressValues = { x: newX, y: newY };

      setPercent(next);
      onChange?.(next);
    },
    [trackY, percent.y, onChange],
  );

  // ── Mouse events ────────────────────────────────────────────
  const onMouseDown = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      isDragging.current = true;
      calculate(e.clientX, e.clientY);
    },
    [calculate],
  );

  const onMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging.current) return;
      calculate(e.clientX, e.clientY);
    },
    [calculate],
  );

  const onMouseUp = useCallback(() => {
    isDragging.current = false;
  }, []);

  // ── Touch events ─────────────────────────────────────────────
  const onTouchStart = useCallback(
    (e: TouchEvent) => {
      isDragging.current = true;
      const touch = e.touches[0];
      calculate(touch.clientX, touch.clientY);
    },
    [calculate],
  );

  const onTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!isDragging.current) return;
      e.preventDefault(); // prevent scroll while dragging
      const touch = e.touches[0];
      calculate(touch.clientX, touch.clientY);
    },
    [calculate],
  );

  const onTouchEnd = useCallback(() => {
    isDragging.current = false;
  }, []);

  // ── Attach / detach ──────────────────────────────────────────
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Element-level (start drag)
    el.addEventListener("mousedown", onMouseDown);
    el.addEventListener("touchstart", onTouchStart, { passive: true });

    // Document-level (track drag even if cursor leaves element)
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    document.addEventListener("touchmove", onTouchMove, { passive: false });
    document.addEventListener("touchend", onTouchEnd);

    return () => {
      el.removeEventListener("mousedown", onMouseDown);
      el.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd);
    };
  }, [
    onMouseDown,
    onMouseMove,
    onMouseUp,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
  ]);

  return [ref, percent];
};

export default useProgress;
